import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
} from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/types';
import { apiClient } from './client';

export interface PasskeyCredential {
  id: string;
  credentialId: string;
  deviceLabel: string | null;
  transports: string[];
  createdAt: string;
  lastUsedAt: string | null;
  backupEligible: boolean;
  backupState: boolean;
  aaguid: string | null;
}

export interface WebAuthnLoginResponse {
  accessToken: string;
  user: {
    id: string;
    username: string;
    displayName: string;
    avatarUrl: string | null;
    globalRole: string;
    emailVerified: boolean;
  };
}

export const webauthnApi = {
  isSupported: () => browserSupportsWebAuthn(),
  platformAvailable: () => platformAuthenticatorIsAvailable(),

  /** Full registration ceremony for the currently authenticated user. */
  async register(deviceLabel?: string): Promise<PasskeyCredential> {
    const { data: options } = await apiClient.post<PublicKeyCredentialCreationOptionsJSON>(
      '/api/webauthn/register/begin',
    );
    const attResp = await startRegistration(options);
    const { data: cred } = await apiClient.post<PasskeyCredential>(
      '/api/webauthn/register/finish',
      { response: attResp, deviceLabel },
    );
    return cred;
  },

  /**
   * Full login ceremony.
   *
   * If `emailOrUsername` is provided, the server scopes the challenge to that
   * user's credentials. Otherwise we issue a discoverable / usernameless
   * challenge — the browser picks the credential.
   */
  async login(emailOrUsername?: string): Promise<WebAuthnLoginResponse> {
    const { data: options } = await apiClient.post<PublicKeyCredentialRequestOptionsJSON>(
      '/api/webauthn/authenticate/begin',
      emailOrUsername ? { emailOrUsername } : {},
    );
    const assertion = await startAuthentication(options);
    const { data } = await apiClient.post<WebAuthnLoginResponse>(
      '/api/webauthn/authenticate/finish',
      { response: assertion },
    );
    return data;
  },

  list: () =>
    apiClient.get<PasskeyCredential[]>('/api/webauthn/credentials').then((r) => r.data),

  remove: (id: string) =>
    apiClient.delete(`/api/webauthn/credentials/${id}`).then(() => undefined),

  rename: (id: string, label: string) =>
    apiClient
      .patch<{ id: string; deviceLabel: string | null }>(
        `/api/webauthn/credentials/${id}`,
        { label },
      )
      .then((r) => r.data),
};
